import { useCallback, useRef, useState } from 'react';

import { openMessageStream } from './api';

/*
 * Reads the Server-Sent Event stream the backend returns for one question.
 *
 * Frames (see ai_chat/agent.py):
 *   start  {user_message_id}
 *   delta  {text}                       — append to the answer as it is written
 *   tool   {name, status, ms}           — which tool is running right now
 *   done   {message_id, input_tokens, output_tokens, iterations, elapsed_ms}
 *   error  {message}
 *
 * Lines beginning with ":" are keep-alive comments sent while a slow tool runs;
 * they exist to stop the load balancer closing an idle connection and carry no
 * data.
 */

const parseFrame = (raw) => {
  let event = 'message';
  const dataLines = [];
  raw.split('\n').forEach((line) => {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  });
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('')) };
  } catch (err) {
    return null;
  }
};

export default function useChatStream({ onFrame }) {
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const send = useCallback(
    async (conversationId, message, model) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);

      try {
        const response = await openMessageStream(
          conversationId,
          message,
          model,
          controller.signal
        );

        if (!response.ok || !response.body) {
          const body = await response.json().catch(() => ({}));
          onFrame({
            event: 'error',
            data: { message: body.detail || `Request failed (${response.status})` },
          });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Frames are separated by a blank line. Anything after the last
          // separator is a partial frame and stays in the buffer.
          const parts = buffer.split('\n\n');
          buffer = parts.pop();
          parts.forEach((part) => {
            if (!part.trim() || part.trim().startsWith(':')) return;
            const frame = parseFrame(part);
            if (frame) onFrame(frame);
          });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          onFrame({ event: 'error', data: { message: err.message } });
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [onFrame]
  );

  return { send, stop, streaming };
}
